import { format } from 'date-fns';
import type { TutorSlot } from '@/lib/schemas/booking';

export interface BookableSlot {
  startUtc: Date;
  endUtc: Date;
}

export const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
export type DayKey = (typeof DAY_KEYS)[number];

export const WEEKDAY_PICKER: Array<{ key: DayKey; short: string; full: string }> = [
  { key: 'mon', short: 'Mon', full: 'Monday' },
  { key: 'tue', short: 'Tue', full: 'Tuesday' },
  { key: 'wed', short: 'Wed', full: 'Wednesday' },
  { key: 'thu', short: 'Thu', full: 'Thursday' },
  { key: 'fri', short: 'Fri', full: 'Friday' },
  { key: 'sat', short: 'Sat', full: 'Saturday' },
  { key: 'sun', short: 'Sun', full: 'Sunday' },
];

/**
 * Narrows the slots returned by GET /tutors/:id/slots to those starting on
 * `dateStr` in the parent's local timezone, excluding slots already past.
 */
export function slotsForLocalDate(slots: TutorSlot[], dateStr: string): BookableSlot[] {
  const now = new Date();
  return slots
    .map((s) => ({ startUtc: new Date(s.startUtc), endUtc: new Date(s.endUtc) }))
    .filter((s) => format(s.startUtc, 'yyyy-MM-dd') === dateStr && s.startUtc > now)
    .sort((a, b) => a.startUtc.getTime() - b.startUtc.getTime());
}

export function formatSlotDuration(slot: BookableSlot): string {
  const minutes = Math.round((slot.endUtc.getTime() - slot.startUtc.getTime()) / 60000);
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? '1 Hour' : `${hours} Hours`;
  }
  return `${minutes} Min`;
}

export function frequencyUsesDays(frequency: string): boolean {
  return frequency === 'weekly' || frequency === 'biweekly' || frequency === 'custom';
}

export function formatFrequencyAdverb(pattern: string): string {
  switch (pattern) {
    case 'daily':
      return 'daily';
    case 'weekly':
      return 'weekly';
    case 'biweekly':
      return 'bi-weekly';
    case 'monthly':
      return 'monthly';
    case 'custom':
      return 'on custom days';
    default:
      return pattern;
  }
}

/** "Friday" | "Friday and Saturday" | "Monday, Wednesday and Friday" */
export function formatDayList(days: DayKey[]): string {
  const ordered = WEEKDAY_PICKER.filter((d) => days.includes(d.key)).map((d) => d.full);
  if (ordered.length <= 1) return ordered[0] ?? '';
  return `${ordered.slice(0, -1).join(', ')} and ${ordered[ordered.length - 1]}`;
}

/**
 * Client mirror of the backend's expandRecurrenceSlots day-walk: starting at
 * the first slot, include dates per the frequency rule until `count` classes
 * are reached. Used to show the real first → last class range in the summary.
 */
export function expandSeriesDates(
  startUtc: Date,
  frequency: string,
  days: DayKey[],
  count: number,
): Date[] {
  if (count < 1) return [];
  if (frequency === 'custom' && days.length === 0) return [];

  const dates: Date[] = [];
  const current = new Date(startUtc);
  const hardStop = startUtc.getTime() + 2 * 366 * 24 * 60 * 60 * 1000; // safety: 2 years

  while (dates.length < count && current.getTime() < hardStop) {
    const dow = DAY_KEYS[current.getUTCDay()] as DayKey;
    let include = false;

    switch (frequency) {
      case 'daily':
        include = true;
        break;
      case 'weekly':
        include = days.length > 0 ? days.includes(dow) : true;
        break;
      case 'biweekly': {
        // Mirrors the backend's even-week-of-month rule exactly.
        const weekNum = Math.floor(
          (current.getUTCDate() - 1) / 7 +
            (current.getUTCDay() <
            new Date(current.getFullYear(), current.getUTCMonth(), 1).getUTCDay()
              ? 1
              : 0),
        );
        if (weekNum % 2 === 0) {
          include = days.length > 0 ? days.includes(dow) : true;
        }
        break;
      }
      case 'monthly':
        include = current.getUTCDate() === startUtc.getUTCDate();
        break;
      case 'custom':
        include = days.includes(dow);
        break;
    }

    if (include) {
      dates.push(new Date(current));
    }
    current.setUTCDate(current.getUTCDate() + 1);
  }

  return dates;
}

export function formatDateReadable(dateStr: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function getBookingSummaryText({
  isSeriesMode,
  seriesDates,
  selectedSlot,
  repeatPattern,
  seriesNeedsDays,
  seriesDays,
  date,
  verb = 'Booking',
}: {
  isSeriesMode: boolean;
  seriesDates: Date[];
  selectedSlot: BookableSlot;
  repeatPattern: string;
  seriesNeedsDays: boolean;
  seriesDays: DayKey[];
  date: string;
  verb?: string;
}): string {
  if (!selectedSlot) return '';
  
  const durationText = formatSlotDuration(selectedSlot);
  const timeText = `${format(selectedSlot.startUtc, 'h:mm a')} – ${format(selectedSlot.endUtc, 'h:mm a')}`;
  
  if (isSeriesMode && seriesDates.length > 0) {
    if (seriesDates.length === 1) {
      return `${verb} 1 class for ${format(seriesDates[0]!, 'd MMMM yyyy')}, ${timeText} (${durationText}).`;
    } else {
      const daysText = seriesNeedsDays && seriesDays.length > 0 ? ` every ${formatDayList(seriesDays)}` : '';
      const startText = format(seriesDates[0]!, 'd MMMM yyyy');
      const endText = format(seriesDates[seriesDates.length - 1]!, 'd MMMM yyyy');
      return `${verb} ${seriesDates.length} classes ${formatFrequencyAdverb(repeatPattern)}${daysText}, from ${startText} to ${endText}, ${timeText} (${durationText} each).`;
    }
  } else {
    return `${verb} 1 class for ${formatDateReadable(date)}, ${timeText} (${durationText}).`;
  }
}
